// v1 placement-redesign: confirmation popover anchored next to a marker
// when the user clicks its × button. Background click = cancel. Confirm
// fires onConfirm; the parent removes the field from working state.
import { useEffect, type CSSProperties } from 'react';
import { APP } from '../app/tokens';

export interface DeleteFieldConfirmProps {
  /** Bounding rect of the clicked marker. The popover positions itself
   *  to the right of (or below, if no horizontal room) the anchor. */
  anchorRect: DOMRect;
  fieldLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const POPOVER_WIDTH = 220;
const POPOVER_HEIGHT_ESTIMATE = 92;
const GAP = 8;

export function DeleteFieldConfirm({
  anchorRect,
  fieldLabel,
  onConfirm,
  onCancel,
}: DeleteFieldConfirmProps) {
  // Esc to cancel — matches every other dismissable popover in the viewer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Position to the right of the marker by default; if that runs off the
  // viewport, flip below it.
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const wantsLeft = anchorRect.right + GAP + POPOVER_WIDTH;
  const fitsRight = wantsLeft <= viewportW - 8;
  const left = fitsRight
    ? anchorRect.right + GAP
    : Math.max(8, Math.min(anchorRect.left, viewportW - POPOVER_WIDTH - 8));
  const wantsTop = fitsRight ? anchorRect.top : anchorRect.bottom + GAP;
  const top = Math.max(8, Math.min(wantsTop, viewportH - POPOVER_HEIGHT_ESTIMATE - 8));

  return (
    <>
      {/* Background catches outside-clicks for cancel. Transparent — no scrim
          so the user can still see what they're deleting. */}
      <div
        onClick={onCancel}
        style={{
          position: 'fixed', inset: 0,
          zIndex: 199,
          background: 'transparent',
        }}
      />
      <div
        role="dialog"
        aria-label="Delete field"
        style={{
          ...CARD,
          position: 'fixed',
          left,
          top,
          width: POPOVER_WIDTH,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={TITLE}>Delete this {fieldLabel}?</div>
        <div style={SUB}>You can add it back any time from the toolbar.</div>
        <div style={ACTIONS}>
          <button type="button" onClick={onCancel} style={SECONDARY_BTN}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} style={DESTRUCTIVE_BTN}>
            Delete
          </button>
        </div>
      </div>
    </>
  );
}

const CARD: CSSProperties = {
  zIndex: 200,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  borderRadius: 10,
  backgroundColor: 'var(--app-bg-card-body)',
  border: `1px solid ${APP.border}`,
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
  fontFamily: APP.fontFamily,
  animation: 'cornerPromptSlideUp 160ms ease-out',
};

const TITLE: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: APP.textPrimary,
};

const SUB: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  lineHeight: 1.35,
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  gap: 6,
  marginTop: 4,
  justifyContent: 'flex-end',
};

const SECONDARY_BTN: CSSProperties = {
  padding: '6px 10px',
  borderRadius: 7,
  border: `1px solid ${APP.border}`,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  color: APP.textPrimary,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: APP.fontFamily,
};

const DESTRUCTIVE_BTN: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 7,
  border: 'none',
  cursor: 'pointer',
  backgroundColor: '#C84F6E',
  color: '#FFFFFF',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
};
